/**
 * Theme manager (ui-audit B2): light / dark / system.
 * Persists to localStorage; applied on boot before first paint (main.tsx)
 * so there is no flash of the wrong theme.
 */

export type Theme = 'light' | 'dark' | 'system';

const KEY = 'suturia:theme';

export function getStoredTheme(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return theme;
}

export function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  const color = resolved === 'dark' ? '#1A1D1C' : '#F6F5F1';
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', color);
}

export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    /* storage unavailable */
  }
  applyTheme(theme);
}

/** Boot-time init: apply stored theme + follow OS changes when set to system. */
export function initTheme(): void {
  applyTheme(getStoredTheme());
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (getStoredTheme() === 'system') applyTheme('system');
    });
  } catch {
    /* older browsers */
  }
}
